import API from "../http-common";
import axios from "axios";

import { debugLog } from "../utils/debugUtils";

const getAll = () => {
	return API.get("/baselines");
};

const get = (id) => {
	return API.get(`/baselines/${id}`);
};

const dump = (id) => {
	return API.get(`/baselines/dump/${id}`);
};

const create = (data) => {
	return API.post("/baselines", data);
};

const update = (id, data) => {
	return API.put(`/baselines/${id}`, data);
};

const importBaseline = (data) => {
	return API.post(`/baselines/import`, data);
};

const copy = (data) => {
	return API.post("/baselines/copy", data);
};

const remove = (data) => {
	// return API.delete(`/baselines/${id}`);
	return API.post("/baselines/delete", data);
};

const findByKeyword = (keyword) => {
	return API.get(`/baselines?kw=${keyword}`);
};

const getItem = (id) => {
	return API.get(`/baselines/item/${id}`);
};

const createItem = (data) => {
	return API.post("/baselines/item", data);
};

const getItemIngestProfiles = () => {
	return API.get("/baselines/items/ingest-profiles");
};

const updateItem = (id, data) => {
	return API.put(`/baselines/item/${id}`, data);
};

const reviewItemIngest = (id, file, sourceProfileId, options = {}) => {
	const formData = new FormData();
	formData.append("file", file);
	if (sourceProfileId) {
		formData.append("sourceProfileId", sourceProfileId);
	}
	if (options.reportMode) {
		formData.append("reportMode", options.reportMode);
	}
	if (options.maxPreviewItems != null) {
		formData.append("maxPreviewItems", String(options.maxPreviewItems));
	}
	if (options.useMappingFallback != null) {
		formData.append("useMappingFallback", String(!!options.useMappingFallback));
	}
	return API.post(`/baselines/${id}/items/ingest-review`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
};

const getItemIngestParseJobStatus = (id, jobId) => {
	return API.get(`/baselines/${id}/items/ingest-parse-jobs/${jobId}`);
};

const getItemIngestParseJobResult = (id, jobId) => {
	return API.get(`/baselines/${id}/items/ingest-parse-jobs/${jobId}/result`);
};

const downloadItemIngestParseJobResult = (id, jobId) => {
	return API.get(`/baselines/${id}/items/ingest-parse-jobs/${jobId}/download`, {
		responseType: "blob",
	});
};

const reviewItemIngestGeneratedJson = (id, generatedJson, options = {}) => {
	return API.post(`/baselines/${id}/items/ingest-review-json`, {
		generatedJson,
		filename: options.filename,
		sourceProfileId: options.sourceProfileId,
		stage2Result: options.stage2Result,
		intakeResult: options.intakeResult,
		reportMode: options.reportMode,
		maxPreviewItems: options.maxPreviewItems,
		useMappingFallback: options.useMappingFallback,
	});
};

const getItemIngestParseJobDownloadUrl = (id, jobId) => {
	const serverUri = String(process.env.REACT_APP_SERVER_URI || "").trim().replace(/\/$/, "");
	const rawExplicit =
		(typeof window !== "undefined" && window.__RAVEN_API_BASE_URL) ||
		process.env.REACT_APP_API_BASE_URL ||
		"";
	const explicit = String(rawExplicit).trim().replace(/\/$/, "");
	if (explicit) {
		if (/^https?:\/\//i.test(explicit)) {
			return `${explicit}/baselines/${id}/items/ingest-parse-jobs/${jobId}/download`;
		}
		if (explicit.startsWith("/") && serverUri) {
			const origin = /^https?:\/\//i.test(serverUri)
				? serverUri
				: `${typeof window !== "undefined" ? window.location.protocol : "http:"}//${serverUri}`;
			return `${origin}${explicit}/baselines/${id}/items/ingest-parse-jobs/${jobId}/download`;
		}
		if (typeof window !== "undefined") {
			return `${window.location.origin}${explicit}/baselines/${id}/items/ingest-parse-jobs/${jobId}/download`;
		}
	}

	if (serverUri) {
		const origin = /^https?:\/\//i.test(serverUri)
			? serverUri
			: `${typeof window !== "undefined" ? window.location.protocol : "http:"}//${serverUri}`;
		return `${origin}/api/baselines/${id}/items/ingest-parse-jobs/${jobId}/download`;
	}

	return `/api/baselines/${id}/items/ingest-parse-jobs/${jobId}/download`;
};

const importItems = (id, data) => {
	const endpointPath = `/baselines/${id}/items/import`;

	const buildLocalFallbackBases = () => {
		if (typeof window === "undefined") return [];

		const host = window.location.hostname;
		const protocol = window.location.protocol || "http:";
		const isLocalHost = host === "localhost" || host === "127.0.0.1";
		if (!isLocalHost) return [];

		const serverUri = String(process.env.REACT_APP_SERVER_URI || "").trim().replace(/\/$/, "");
		const envCandidate = serverUri
			? /^https?:\/\//i.test(serverUri)
				? `${serverUri}/api`
				: `${protocol}//${serverUri}/api`
			: "";

		const candidates = [
			envCandidate,
			`${window.location.origin}/api`,
			`${protocol}//localhost:8080/api`,
			`${protocol}//127.0.0.1:8080/api`,
			`${protocol}//localhost:8081/api`,
			`${protocol}//127.0.0.1:8081/api`,
		].filter(Boolean);

		return Array.from(new Set(candidates.map((x) => x.replace(/\/$/, ""))));
	};

	const postToBase = (baseUrl) =>
		axios.post(`${baseUrl}${endpointPath}`, data, {
			baseURL: undefined,
			withCredentials: true,
			headers: {
				"Content-Type": "application/json",
			},
		});

	return API.post(endpointPath, data).catch(async (error) => {
		if (error?.response) {
			throw error;
		}

		const fallbackBases = buildLocalFallbackBases();
		for (let i = 0; i < fallbackBases.length; i++) {
			const baseUrl = fallbackBases[i];
			try {
				const response = await postToBase(baseUrl);
				debugLog(2, "[BaselineService][FLOW] importItems local fallback succeeded", {
					baseUrl,
				});
				return response;
			} catch (retryError) {
				if (retryError?.response) {
					throw retryError;
				}
			}
		}

		throw error;
	});
};

const copyItem = (data) => {
	return API.post("/baselines/item/copy", data);
};

const deleteItem = (data) => {
	return API.post("/baselines/item/delete", data);
};

const findByUuid = (uuid) => {
	return API.get(`/baselines/uuid/${uuid}`);
};

const updateItemThumbnails = (id) => {
	// Bulk rebuild for all items in a baseline
	return API.post(`/baselines/${id}/thumbnails/rebuild`, {});
};

// Upload or update a baseline item photo
const uploadItemPhoto = (id, file) => {
	const formData = new FormData();
	formData.append("file", file);
	return API.post(`/baselines/item/${id}/photo`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
};

// Remove a baseline item photo
const removeItemPhoto = (id) => {
	return API.delete(`/baselines/item/${id}/photo`);
};

const updateStatus = (id, status_id, updatedBy) => {
	// Could be PATCH or PUT
	// return http.patch(`/baselines/${id}/status`, { status });
	// or PUT depending on your convention
	return API.put(`/baselines/${id}/status`, { status_id, updatedBy });
};

const updateItemStatus = (id, status_id, updatedBy) => {
	return API.put(`/baselines/item/${id}/status`, { status_id, updatedBy });
};

const BaselineService = {
	getAll,
	get,
	dump,
	create,
	update,
	copy,
	remove,
	findByKeyword,
	getItem,
	createItem,
	getItemIngestProfiles,
	updateItem,
	reviewItemIngest,
	reviewItemIngestGeneratedJson,
	getItemIngestParseJobStatus,
	getItemIngestParseJobResult,
	downloadItemIngestParseJobResult,
	getItemIngestParseJobDownloadUrl,
	importItems,
	copyItem,
	deleteItem,
	importBaseline,
	findByUuid,
	updateItemThumbnails,
	updateStatus,
	updateItemStatus,
	uploadItemPhoto,
	removeItemPhoto,
};

export default BaselineService;
